import {
  useContext, useState, useEffect, ComponentType,
} from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useSelector, useDispatch } from 'react-redux';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Paper from '@mui/material/Paper';
import InputBase from '@mui/material/InputBase';
import IconButton from '@mui/material/IconButton';
import SearchIcon from '@mui/icons-material/Search';
import Link from '@mui/material/Link';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import EnhancedTable, { EnhancedTableProps } from '#components/EnhancedTable';
import EnhancedAutocomplete from '#components/EnhancedAutocomplete';
import useForm from '#utils/use-form';
import { FeedbackUIContext } from '#context/feedback-ui-context';
import { collectionValidation } from '#common/validation';
import Switch from '@mui/material/Switch';
import Tooltip from '@mui/material/Tooltip';
import Autocomplete, { AutocompleteRenderInputParams } from '@mui/material/Autocomplete';
import { useDebounce } from 'use-debounce';
import TitleBar, { TitleBarItemTraveler } from '#components/TitleBar';

import {
  useSaveCollectionMutation,
} from '#services/collection';
import {
  useGetProductsForSearchingQuery,
} from '#services/product';
import URLSearchParamsEx from '#utils/url-ex';
import {
  useData,
  getInitialState,
} from './Edit.utils';
import {
  clearSearchProducts,
} from './Edit.slice';

import {
  setFilter as setUserFilter,
  setFilterKey as setUserFilterKey,
} from '../user/list.slice';

interface RelatedProduct {
  id: number;
  name: string;
  collectionIds: number[];
  newCollections?: string[];
  [key: string]: unknown;
}

interface ProductChange {
  productId: number;
  event: string;
  collectionIds?: number[] | undefined;
  newCollections?: string[] | undefined;
  // The original delete handler (incorrectly) probes these on stored changes;
  // keep them optional so the legacy lookup still type-checks.
  name?: string;
  value?: unknown;
}

interface ProductChangeParams {
  productId: number;
  productName?: string;
  event: string;
  collectionIds?: number[];
  newCollections?: string[];
  row?: RelatedProduct;
}

interface SearchProduct {
  id: number;
  name: string;
  collectionIds: number[];
}

// The "add products" Autocomplete drives a string value against object options,
// so relax its prop typing while annotating its own callbacks.
const LooseAutocomplete = Autocomplete as unknown as ComponentType<Record<string, unknown>>;

export default function CollectionEdit() {
  const {
    showBackdrop,
    hideBackdrop,

    simpleSnackbar,
  } = useContext(FeedbackUIContext)!;

  const {
    values,
    isDirty,
    isValid,
    errors,
    handleChange,
    handleSubmit,
    addSubmitListener,
    resetForm,
  } = useForm({
    initialState: getInitialState(),
    validations: collectionValidation,
  });

  useData({ resetForm });

  const location = useLocation();
  const navigate = useNavigate();
  /*
    changes structure:
    [{
      event: 'set|delete',
      productId: 5,
      collectionIds: [4,5],
      newCollection: ['col1'],
    }]
  */
  const [productChanges, setProductChanges] = useState<ProductChange[]>([]);
  const [saveCollection] = useSaveCollectionMutation();
  const [filter, setFilter] = useState('');
  const [addProductFilter, setAddProductFilter] = useState('');
  const [selectedAddProduct, setSelectedAddProduct] = useState<SearchProduct | null>(null);
  const [debouncedAddProductFilter] = useDebounce(addProductFilter, 300);
  const [newProductList, setNewProductList] = useState<RelatedProduct[]>([]);
  const dispatch = useDispatch();

  const listFilter = useSelector((state: { collectionList: { filter: string } }) => state.collectionList.filter);
  const order = useSelector((state: { collectionList: { order: string } }) => state.collectionList.order);
  const orderBy = useSelector((state: { collectionList: { orderBy: string } }) => state.collectionList.orderBy);
  const searchProducts = useSelector((state: { collectionEdit: { searchProducts: SearchProduct[] } }) => state.collectionEdit.searchProducts);
  const urlParams = new URLSearchParamsEx(location as unknown as Record<string, unknown>);

  const query = useGetProductsForSearchingQuery({
    filter: debouncedAddProductFilter,
  }, {
    refetchOnMountOrArgChange: true,
  });

  useEffect(() => {
    simpleSnackbar(query as unknown as Parameters<typeof simpleSnackbar>[0]);
  }, [query.error]);

  const isEdit = Boolean(urlParams.values.collectionId || urlParams.values.collectionIndex);

  const handleSaveCollection = (formValues: Record<string, unknown>, formIsValid: boolean) => {
    if (!formIsValid) {
      return;
    }

    showBackdrop();
    const postData: Record<string, unknown> = {
      id: formValues.id,
      collectionIndex: urlParams.values.collectionIndex,
      filter: listFilter,
      order,
      orderBy,
    };
    if (!isDirty && !productChanges.length) {
      return;
    }

    if (productChanges.length) {
      postData.productChanges = productChanges;
    }

    if (isDirty) {
      Object.assign(postData, formValues);
      delete postData.availableCollections;
      delete postData.relatedProducts;
    }

    void saveCollection(postData)
      .then((res) => {
        const hasError = simpleSnackbar(res as unknown as Parameters<typeof simpleSnackbar>[0], `Collection "${String(formValues.name)}" was successfully updated.`);

        if (hasError) {
          return;
        }

        resetForm(getInitialState({
          ...(res.data as Record<string, unknown>),
        }));
        setProductChanges([]);
        setNewProductList([]);
      }).finally(() => {
        hideBackdrop();
      });
  };
  addSubmitListener(handleSaveCollection);

  function handleProductChanges({
    productId,
    productName,
    event,
    collectionIds,
    newCollections,
    row,
  }: ProductChangeParams) {
    if (event === 'add' && !productName) {
      throw new Error('productName is required for add event');
    }

    let nextChanges = [...productChanges];
    let nextProductList = [...newProductList];

    const targetChange: ProductChange = nextChanges.find((pc) => pc.productId === productId) || {
      productId,
      event,
      collectionIds,
      newCollections,
    };
    const targetProduct = nextProductList.find((p) => p.id === productId);

    const product: RelatedProduct = {
      id: productId,
      name: productName as string,
      collectionIds: collectionIds || [],
    };

    if (event === 'add') {
      if (targetChange.event === 'delete') {
        nextChanges = nextChanges.filter((nc) => nc.productId !== productId);
        nextProductList = nextProductList.filter((p) => p.id !== productId);
      } else if (!targetProduct && !(values.relatedProducts as RelatedProduct[]).some((rp) => rp.id === productId)) {
        // add if not already added
        nextChanges.push(targetChange);
        nextProductList.push(product);
      }
    } else if (event === 'set') {
      targetChange.collectionIds = (
        collectionIds
          || targetChange.collectionIds
          || row?.collectionIds
      );
      targetChange.newCollections = (
        newCollections
        || targetChange.newCollections
        || row?.newCollections
      );
      nextChanges = nextChanges.filter((nc) => nc.productId !== productId && nc.event !== 'set');
      nextChanges.push(targetChange);
    } else if (event === 'delete') {
      nextChanges = nextChanges.filter((nc) => nc.productId !== productId && nc.event !== 'delete');
      if (targetChange.event === 'delete') {
        nextChanges.push(targetChange);
      }

      if (targetProduct) {
        nextProductList = nextProductList.filter((p) => p.id !== productId);
      }
    } else {
      throw new Error('Unknown handleProductChanges event');
    }

    setProductChanges(nextChanges);
    setNewProductList(nextProductList);
  }

  // new added product
  let relatedProducts: RelatedProduct[] = [...(values.relatedProducts as RelatedProduct[]), ...newProductList];
  relatedProducts = relatedProducts.map((rp) => {
    const find = productChanges.find((pc) => pc.productId === rp.id);
    if (!find) {
      return rp;
    }
    if (find.event === 'delete') {
      return null;
    }

    return {
      ...rp,
      collectionIds: find.collectionIds || rp.collectionIds,
      newCollections: find.newCollections || rp.newCollections,
    };
  }).filter((rp): rp is RelatedProduct => Boolean(rp));

  if (filter) {
    relatedProducts = relatedProducts
      .filter((rp) => rp.name.toLowerCase().includes(filter.toLowerCase().trim()));
  }

  const tableHeaders: EnhancedTableProps['headers'] = [{
    id: 'name',
    label: 'Product',
    renderer: (row) => {
      const product = row as unknown as RelatedProduct;
      return (
        <Box>
          <Link
            component="button"
            onClick={() => {
              void navigate(`/management/product/edit?productId=${product.id}`);
            }}
          >
            {product.name}
          </Link>
        </Box>
      );
    },
  }, {
    id: 'collectionIds',
    label: 'Collections',
    renderer: (row) => {
      const product = row as unknown as RelatedProduct;
      return (
        <EnhancedAutocomplete
          multiple
          disableCloseOnSelect
          size="small"
          options={values.availableCollections as { id: number; name: string }[]}
          // props for enhanced
          error={!!errors.collectionIds}
          lockedIds={[values.id as number]}
          selectedIdsName="collectionIds"
          selectedIds={product.collectionIds || []}
          newValuesName="newCollections"
          newValues={product.newCollections || []}
          onChange={(event, data) => {
            const collectionIdsResult = (data.find((d) => d.name === 'collectionIds')?.value as number[]) || product.collectionIds;
            const newCollectionsResult = (data.find((d) => d.name === 'newCollections')?.value as string[]) || [];
            handleProductChanges({
              productId: product.id,
              event: 'set',
              collectionIds: collectionIdsResult,
              newCollections: newCollectionsResult,
            });
          }}
        />
      );
    },
  }, {
    id: 'action',
    label: 'Action',
    sortable: false,
    renderer: (row) => {
      const product = row as unknown as RelatedProduct;
      const handleDeleteCollectionForProduct = () => {
        const collectionIdsResult = (productChanges.find((d) => d.name === 'collectionIds')?.value as number[]) || product.collectionIds;
        const newCollectionsResult = (productChanges.find((d) => d.name === 'newCollections')?.value as string[]) || [];
        handleProductChanges({
          productId: product.id,
          event: 'delete',
          row: product,
          collectionIds: collectionIdsResult,
          newCollections: newCollectionsResult,
        });
      };

      const disabled = ((product.collectionIds.length + (product.newCollections?.length || 0)) <= 1);

      if (disabled) {
        return (
          <Box>
            <Tooltip title="Products cannot be unassociated; they must be linked to a collection.">
              <span>
                <Link
                  component="button"
                  className="disabled-link"
                >
                  Delete
                </Link>
              </span>
            </Tooltip>
          </Box>
        );
      }

      return (
        <Box>
          <Link
            component="button"
            onClick={handleDeleteCollectionForProduct}
          >
            Delete
          </Link>
        </Box>
      );
    },
  }];

  const tableProps: EnhancedTableProps = {
    name: 'products',
    headers: tableHeaders,
    enableSort: false,
    rowCount: relatedProducts.length,
    rows: relatedProducts,
  };

  const handleTravelOrder = (direction: number) => {
    urlParams.set('collectionIndex', (val) => String((val as number) + direction));
    const search = urlParams.toString();
    void navigate({
      pathname: '/management/collection/edit',
      search,
    });
  };

  return (
    <>
      <TitleBar
        title={`${isEdit ? 'Edit' : 'Add'} Collection`}
        hideActionButtons={!urlParams.values.collectionIndex}
      >
        <TitleBarItemTraveler
          onTravelItem={(direction) => handleTravelOrder(direction)}
          itemIndex={values.collectionIndex as number}
          itemSize={values.total as number}
        />
      </TitleBar>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          bgcolor: 'background.paper',
          borderRadius: 1,
        }}
      >
        <Box />

        <FormControlLabel
          sx={{
            mx: 1,
          }}
          control={(
            <Switch
              checked={!values.inactivated}
              onChange={(e) => {
                e.target.checked = !e.target.checked;
                handleChange(e);
              }}
              name="inactivated"
              color="primary"
            />
          )}
          label="Activated"
        />
      </Box>

      <Box>
        <TextField
          required
          fullWidth
          size="small"
          name="name"
          label="Name"
          value={values.name}
          error={!!errors.name}
          onChange={handleChange}
        />
      </Box>

      <Box
        sx={{
          bgcolor: 'background.paper',
          borderRadius: 1,
        }}
      >
        <TextField
          label="Description"
          multiline
          fullWidth
          rows={5}
          value={values.description}
          onChange={(event) => {
            const newText = event.target.value;

            handleChange(event, {
              name: 'description',
              value: newText,
            });
          }}
        />
      </Box>

      {
        isEdit && (
          <>
            <Box sx={{ typography: 'h7' }}>
              Products
            </Box>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                '& .MuiTextField-root': { mb: 0 },
              }}
            >
              <Paper
                elevation={0}
                variant="outlined"
                square
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  width: 400,
                }}
              >
                <IconButton type="button" sx={{ p: '10px' }} aria-label="search">
                  <SearchIcon />
                </IconButton>
                <InputBase
                  sx={{ ml: 1, flex: 1 }}
                  placeholder="Filter Products"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                />
              </Paper>

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'end',
                  align: 'center',
                }}
              >
                <LooseAutocomplete
                  sx={{ width: 200 }}
                  value={debouncedAddProductFilter}
                  size="small"
                  options={searchProducts}
                  noOptionsText="search for product"
                  getOptionLabel={(option: string | SearchProduct) => {
                    if (typeof option === 'string') {
                      return option;
                    }
                    return option.name;
                  }}
                  onChange={(event: unknown, value: SearchProduct | null) => {
                    setSelectedAddProduct(value);
                    if (!value) {
                      setAddProductFilter('');
                      dispatch(clearSearchProducts());
                    }
                  }}
                  onInputChange={(event: unknown, data: string) => {
                    setAddProductFilter(data.trim());
                  }}
                  renderInput={(params: AutocompleteRenderInputParams) => <TextField {...params} label="Add products" />}
                />
                <Button
                  variant="text"
                  disabled={!selectedAddProduct}
                  onClick={() => {
                    const product = selectedAddProduct!;
                    setSelectedAddProduct(null);
                    handleProductChanges({
                      productId: product.id,
                      productName: product.name,
                      event: 'add',
                      collectionIds: [
                        ...product.collectionIds,
                        values.id as number,
                      ].sort(),
                      newCollections: [],
                    });
                    setAddProductFilter('');
                  }}
                >
                  Add
                </Button>
              </Box>
            </Box>
            <Box className="collection-edit-table">
              <EnhancedTable
                {...tableProps}
              />
            </Box>
          </>
        )
      }

      <Box>
        <Box>
          <FormControlLabel
            control={(
              <Checkbox
                name="availableForAllReadonly"
                disabled={values.availableForAll as boolean}
                checked={(values.availableForAllReadonly as boolean) || false}
                onChange={(e) => handleChange(e)}
              />
            )}
            label="Available for all to browse"
          />
        </Box>
        <Box>
          <FormControlLabel
            control={(
              <Checkbox
                name="availableForAll"
                checked={(values.availableForAll as boolean) || false}
                onChange={(e, data) => {
                  const options = [{
                    name: 'availableForAll',
                    value: data,
                  }];
                  if (data) {
                    // if checked,
                    // disable the availableForAllReadonly
                    // and set it to checked
                    options.push({
                      name: 'availableForAllReadonly',
                      value: true,
                    });
                  }
                  handleChange(e, options);
                }}
              />
            )}
            label="Available for all to purchase"
          />
        </Box>
      </Box>

      <Button
        type="button"
        variant="contained"
        disabled={!isValid || (!isDirty && !productChanges.length)}
        onClick={handleSubmit}
      >
        {isEdit ? 'Save' : 'Add'}
      </Button>

      <Box sx={{ mt: 5 }}>
        <Link
          component="button"
          onClick={() => {
            dispatch(setUserFilter(values.name as string));
            dispatch(setUserFilterKey('collectionName'));
            void navigate('/management/user/list');
          }}
        >
          Check the collection authorized customers
        </Link>
      </Box>
    </>
  );
}
